import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchListing, PublicListing } from "../api/publicApi";
import { ArcGISParcelMap } from "../components/ArcGISParcelMap";

const Field: React.FC<{ label: string; value: string | number | null | undefined }> = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <div>
      <dt className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">{label}</dt>
      <dd className="text-sm font-semibold text-gray-800">{value}</dd>
    </div>
  );
};

export const PublicListingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [listing, setListing] = useState<PublicListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchListing(id)
      .then(setListing)
      .catch(() => setError("No se encontró esta propiedad."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-gray-400">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm">Cargando propiedad…</p>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-gray-500">
        <svg className="w-14 h-14 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm">{error || "Propiedad no encontrada."}</p>
        <Link to="/arrendamiento" className="text-sm text-blue-600 hover:underline">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="text-sm text-gray-500 flex items-center gap-2">
            <Link to="/arrendamiento" className="hover:text-blue-600">Terrenos en Arrendamiento</Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-800 font-medium">{listing.subParcelId}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Left: details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-widest mb-1">Sub-Parcela</p>
              <h1 className="text-2xl font-bold text-gray-900">{listing.subParcelId}</h1>
              <p className="text-gray-500 mt-1">{listing.municipio}</p>
            </div>

            {/* Canon highlight */}
            {listing.canon != null && (
              <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
                <p className="text-xs text-green-600 font-medium uppercase tracking-wide mb-1">Canon mensual</p>
                <p className="text-3xl font-bold text-green-700">${listing.canon.toLocaleString()}</p>
                {listing.canonBase != null && listing.canonBase !== listing.canon && (
                  <p className="text-xs text-green-500 mt-1">Canon base: ${listing.canonBase.toLocaleString()}</p>
                )}
              </div>
            )}

            {/* Property details */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Detalles de la Propiedad</h2>
              <dl className="grid grid-cols-2 gap-4">
                <Field label="Cabida" value={listing.cabida != null ? `${listing.cabida.toFixed(2)} cuerdas` : null} />
                <Field label="Uso" value={listing.uso || listing.usoProgramado} />
                <Field label="Zonificación" value={listing.zoning} />
                <Field label="Estatus" value={listing.estatus} />
                <Field label="Municipio" value={listing.municipio} />
                <Field label="Finca" value={listing.fincaId} />
                <Field label="Catastro" value={listing.catastro} />
                <Field label="Adquirida de" value={listing.acquisitionName} />
              </dl>
            </div>

            {/* Description */}
            {listing.description && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Descripción</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{listing.description}</p>
              </div>
            )}

            {/* CTA */}
            <div className="bg-blue-50 rounded-2xl border border-blue-100 p-5 text-center">
              <p className="text-sm text-blue-700 font-medium mb-1">¿Le interesa esta propiedad?</p>
              <p className="text-xs text-blue-500 mb-4">
                Comuníquese con la Administración de Terrenos para solicitar información o iniciar el proceso de arrendamiento.
              </p>
              <a
                href="mailto:arrendamiento@terrenos.pr.gov"
                className="inline-block px-5 py-2.5 bg-blue-700 text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition"
              >
                Solicitar información
              </a>
            </div>
          </div>

          {/* Right: map */}
          <div className="lg:col-span-3">
            {listing.parcelId ? (
              <ArcGISParcelMap fincaId={listing.parcelId} height="520px" />
            ) : (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 flex items-center justify-center" style={{ height: "520px" }}>
                <div className="text-center text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <p className="text-sm">Mapa no disponible</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
